'use client'

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'
import { Hand, MicOff, SendHorizontal } from 'lucide-react'
import { useVoiceRecorder, type VoiceRecorder, type VoiceRecording } from '@/hooks/use-voice-recorder'
import { SANTA_CHAT_INPUT_LIMITS } from '@/server/domain/santa-chat'
import { LockedMicButton, MicButton, TOGGLE_KEYS, type MicHandlers } from './mic-button'
import { RecordingHint } from './recording-hint'

export interface ComposerLabels {
  placeholder: string
  holdToTalk: string
  recording: string
  slideToCancel: string
  micDenied: string
  send: string
  waitForSanta: string
}

/**
 * Qué hace el micrófono: grabar una nota (el chat del niño) o, en un chat sin
 * notas de voz (el público), no grabar ni pedir permiso y avisar con `onLocked`.
 */
export type VoiceInput =
  | { kind: 'record'; onSend: (recording: VoiceRecording) => void }
  | { kind: 'locked'; onLocked: () => void }

interface ComposerProps {
  labels: ComposerLabels
  disabled: boolean
  /** Papá Noel está contestando: el micrófono y el envío esperan. */
  busy: boolean
  /** El niño aún no ha mandado nada nunca: el micrófono que graba enseña cómo se usa. */
  firstTime: boolean
  onSendText: (text: string) => void
  voice: VoiceInput
}

/** Un toque más corto no lleva voz: se descarta en vez de mandar casi silencio al proveedor. */
const MIN_RECORDING_SECONDS = 0.5
/** Distancia (px) que hay que deslizar a la izquierda para descartar la nota. */
const SLIDE_CANCEL_PX = 80
/** Lo que se ve el recordatorio de «mantén pulsado» tras un toque que no grabó nada. */
const HOLD_HINT_MS = 2500

/**
 * Un aviso que se ve un rato y se va solo. `count` cambia en cada `show()`:
 * sirve de `key` para repetir una animación aunque el aviso ya estuviera.
 */
function useTimedFlag(ms: number) {
  const [count, setCount] = useState(0)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (!visible) return
    const timer = setTimeout(() => setVisible(false), ms)
    return () => clearTimeout(timer)
  }, [visible, count, ms])
  return {
    visible,
    count,
    show: () => {
      setVisible(true)
      setCount((value) => value + 1)
    },
    hide: () => setVisible(false),
  }
}

/**
 * Mantener pulsado para grabar, soltar para enviar, deslizar a la izquierda
 * para cancelar. Con teclado, Espacio/Intro empieza y termina la grabación.
 */
function useHoldToTalk(onSend: (recording: VoiceRecording) => void) {
  const pressedRef = useRef(false)
  const originXRef = useRef<number | null>(null)
  // Un toque (o soltar antes de que arranque el micro, p. ej. con el aviso de
  // permiso de iOS) no graba nada: sin este aviso el botón parece muerto.
  const holdHint = useTimedFlag(HOLD_HINT_MS)
  // Entre pulsar y que el micro arranque (el aviso de permiso puede tardar): el botón no parece muerto.
  const [arming, setArming] = useState(false)
  const recorder: VoiceRecorder = useVoiceRecorder({
    maxSeconds: SANTA_CHAT_INPUT_LIMITS.maxAudioSeconds,
    minSeconds: MIN_RECORDING_SECONDS,
    onLimit: (recording) => {
      // El corte de los 30 s ya envió: soltar después no debe hacer nada.
      pressedRef.current = false
      onSend(recording)
    },
  })

  const begin = (originX: number | null) => {
    pressedRef.current = true
    originXRef.current = originX
    holdHint.hide()
    setArming(true)
    // Un toque rápido puede soltar antes de que el micrófono arranque; y si
    // arrancar falla, el grabador ya pasó a `denied` y se ofrece el texto.
    recorder
      .start()
      .catch(() => {})
      .finally(() => {
        setArming(false)
        if (!pressedRef.current) recorder.cancel()
      })
  }

  const release = async () => {
    if (!pressedRef.current) return
    pressedRef.current = false
    const recording = await recorder.stop()
    if (recording) onSend(recording)
    else holdHint.show()
  }

  const abort = () => {
    pressedRef.current = false
    recorder.cancel()
  }

  const slidLeft = (clientX: number) =>
    originXRef.current !== null && originXRef.current - clientX > SLIDE_CANCEL_PX

  const handlers: MicHandlers = {
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      try {
        event.currentTarget.setPointerCapture(event.pointerId)
      } catch {
        // Sin captura (jsdom, puntero ya liberado): soltar fuera envía igual.
      }
      begin(Number.isFinite(event.clientX) ? event.clientX : null)
    },
    onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
      if (pressedRef.current && slidLeft(event.clientX)) abort()
    },
    // Sin `onPointerLeave`: con la captura, soltar fuera ya llega como `pointerup`; y en
    // iOS, al cerrarse el teclado el botón se mueve bajo el dedo y el leave cortaba la nota.
    onPointerUp: () => void release(),
    onPointerCancel: abort,
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (!TOGGLE_KEYS.has(event.key)) return
      event.preventDefault()
      if (event.repeat) return
      if (pressedRef.current) void release()
      else begin(null)
    },
  }
  return { recorder, handlers, showHoldHint: holdHint.visible, arming }
}

/** El borrador del campo de texto y su envío (que espera si `blocked`). */
function useTextDraft(onSend: (text: string) => void, blocked: boolean) {
  const [text, setText] = useState('')
  const submit = (event: FormEvent) => {
    event.preventDefault()
    const value = text.trim()
    if (!value || blocked) return
    setText('')
    onSend(value)
  }
  return { text, setText, submit, hasText: text.trim() !== '' }
}

interface ComposerHintsProps {
  labels: ComposerLabels
  denied: boolean
  waiting: boolean
  holding: boolean
  coaching: boolean
}

/** Como mucho un aviso sobre el compositor, por prioridad. */
function ComposerHints({ labels, denied, waiting, holding, coaching }: ComposerHintsProps) {
  if (denied) {
    return (
      <p role="alert" className="readable-body mb-2 flex items-center gap-2 text-sm text-destructive">
        <MicOff aria-hidden className="size-6 shrink-0" />
        {labels.micDenied}
      </p>
    )
  }
  if (waiting) return <StatusHint text={labels.waitForSanta} />
  if (holding) return <StatusHint text={labels.holdToTalk} />
  if (coaching) {
    return (
      <p className="readable-body mb-2 flex items-center justify-end gap-2 text-sm font-semibold text-accent">
        {labels.holdToTalk}
        <Hand aria-hidden className="size-6 rotate-180 motion-safe:animate-bounce" />
      </p>
    )
  }
  return null
}

function StatusHint({ text }: { text: string }) {
  return (
    <p role="status" className="readable-body mb-2 text-center text-sm text-muted-foreground">
      {text}
    </p>
  )
}

/** Campo de texto + micrófono (misma altura que el campo) del chat con Papá Noel. */
export function Composer({ voice, ...props }: ComposerProps) {
  if (voice.kind === 'record') return <RecordingComposer {...props} onSendVoice={voice.onSend} />
  return <LockedComposer {...props} onLocked={voice.onLocked} />
}

type CommonProps = Omit<ComposerProps, 'voice'>

interface MicSlot {
  /** Papá Noel está contestando: el toque solo explica que hay que esperar. */
  waiting: { nudge: () => void; count: number } | null
  coaching: boolean
}

interface ComposerFrameProps extends CommonProps {
  recording: boolean
  denied: boolean
  holding: boolean
  renderMic: (slot: MicSlot) => ReactNode
}

/** El campo de texto, los avisos y el envío; el micrófono entra por `renderMic`. */
function ComposerFrame({ labels, disabled, busy, firstTime, onSendText, recording, denied, holding, renderMic }: ComposerFrameProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  // Pulsar el micro mientras Papá Noel contesta: «espera» y el icono dice que no con la cabeza.
  const waitNudge = useTimedFlag(HOLD_HINT_MS)
  // Mientras Papá Noel contesta no se manda otra cosa (el servidor lo rechazaría).
  const sendBlocked = disabled || busy
  const { text, setText, submit, hasText } = useTextDraft(onSendText, sendBlocked)
  const coaching = firstTime && !sendBlocked && !recording && !hasText

  // Sin micrófono, el texto es la vía: el foco va al campo para el adulto.
  useEffect(() => {
    if (denied) inputRef.current?.focus()
  }, [denied])

  return (
    <div className="shrink-0 border-t border-border bg-background px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3">
      <ComposerHints
        labels={labels}
        denied={denied}
        waiting={waitNudge.visible && busy}
        holding={holding}
        coaching={coaching}
      />
      <form onSubmit={submit} className="flex items-center gap-2">
        {recording ? (
          <RecordingHint labels={labels} />
        ) : (
          <input
            ref={inputRef}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={labels.placeholder}
            aria-label={labels.placeholder}
            maxLength={SANTA_CHAT_INPUT_LIMITS.maxTextLength}
            disabled={disabled}
            enterKeyHint="send"
            // 16 px (text-base): por debajo, Safari en iOS hace zoom al enfocar y no lo deshace.
            // El foco se marca con el anillo redondeado, no con el outline cuadrado por defecto.
            className="readable-body h-12 min-w-0 flex-1 rounded-full border border-border bg-input px-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          />
        )}
        {hasText && !recording ? (
          <SendButton label={labels.send} disabled={sendBlocked} />
        ) : (
          renderMic({ waiting: busy ? { nudge: waitNudge.show, count: waitNudge.count } : null, coaching })
        )}
      </form>
    </div>
  )
}

/** Con notas de voz: mantener pulsado para grabar (`useHoldToTalk`). */
function RecordingComposer({ onSendVoice, ...props }: CommonProps & { onSendVoice: (recording: VoiceRecording) => void }) {
  const { recorder, handlers, showHoldHint, arming } = useHoldToTalk(onSendVoice)
  const recording = recorder.state === 'recording'
  return (
    <ComposerFrame
      {...props}
      recording={recording}
      denied={recorder.state === 'denied'}
      holding={showHoldHint}
      renderMic={({ waiting, coaching }) => (
        <MicButton
          label={props.labels.holdToTalk}
          disabled={props.disabled}
          waiting={waiting}
          recording={recording}
          arming={arming && !recording}
          coaching={coaching}
          handlers={handlers}
        />
      )}
    />
  )
}

/** Sin notas de voz: no hay grabadora, ni permiso, ni «mantén pulsado» que enseñar. */
function LockedComposer({ onLocked, ...props }: CommonProps & { onLocked: () => void }) {
  return (
    <ComposerFrame
      {...props}
      firstTime={false}
      recording={false}
      denied={false}
      holding={false}
      renderMic={({ waiting }) => (
        <LockedMicButton label={props.labels.holdToTalk} disabled={props.disabled} waiting={waiting} onPress={onLocked} />
      )}
    />
  )
}

function SendButton({ label, disabled }: { label: string; disabled: boolean }) {
  return (
    <button
      type="submit"
      aria-label={label}
      disabled={disabled}
      className="flex size-12 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50"
    >
      <SendHorizontal aria-hidden className="size-6" />
    </button>
  )
}
